/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronDown, Menu, X } from 'lucide-react';
import { PageRoute } from '../types';
import AuraButton from './AuraButton';

interface NavbarProps {
  currentRoute: PageRoute;
  setRoute: (route: PageRoute) => void;
  scrollToSection: (id: string) => void;
  onBackFromLegal: () => void;
}

const navItems = [
  { id: 'hero', label: 'ホーム' },
  { id: 'about', label: 'Koemoriについて' },
];

const serviceItems: Array<{ label: string; route: PageRoute }> = [
  { label: '楽曲の投稿', route: 'song-submission' },
];

const domilyItems: Array<{ label: string; route: PageRoute }> = [
  { label: 'Domi', route: 'domi' },
];

const legalItems: Array<{ label: string; route: PageRoute }> = [
  { label: 'プライバシーポリシー', route: 'privacy' },
  { label: 'ユーザー規約', route: 'terms' },
  { label: '特定商取引法', route: 'commercial' },
];

const legalTitles: Partial<Record<PageRoute, string>> = {
  recruit: '招募入驻',
  about: 'Koemoriについて',
  privacy: 'プライバシーポリシー',
  terms: 'ユーザー規約',
  commercial: '特定商取引法',
};

type SubmenuKey = 'services' | 'domily' | 'about';

const submenuLabels: Record<SubmenuKey, string> = {
  services: 'ユーザーサービス',
  domily: 'Domilyについて',
  about: 'Koemoriについて',
};

const submenuItems: Record<SubmenuKey, Array<{ label: string; route: PageRoute }>> = {
  services: serviceItems,
  domily: domilyItems,
  about: legalItems,
};

export default function Navbar({ currentRoute, setRoute, scrollToSection, onBackFromLegal }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState<SubmenuKey | null>(null);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 18);
      if (currentRoute !== 'home') return;

      const marker = Math.min(window.innerHeight * 0.35, 240);
      for (const item of navItems) {
        const element = document.getElementById(item.id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top <= marker && rect.bottom >= marker) {
          setActiveSection(item.id);
          break;
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentRoute]);

  const handleNavClick = (sectionId: string) => {
    setMobileMenuOpen(false);
    setSubmenuOpen(null);
    if (currentRoute !== 'home') {
      setRoute('home');
      window.setTimeout(() => scrollToSection(sectionId), 80);
      return;
    }
    scrollToSection(sectionId);
  };

  const handleHome = () => {
    setMobileMenuOpen(false);
    setSubmenuOpen(null);
    setRoute('home');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 30);
  };

  const handleRouteClick = (route: PageRoute) => {
    setMobileMenuOpen(false);
    setSubmenuOpen(null);
    setRoute(route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTopPageClick = (route: SubmenuKey) => {
    setMobileMenuOpen(false);
    setSubmenuOpen(route);
    setRoute(route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const dropdownPanel = (menu: SubmenuKey) => (
    <div className="absolute left-1/2 top-full w-64 -translate-x-1/2 rounded-2xl border border-white/[0.12] bg-[#090B0B]/97 p-4 shadow-[0_24px_70px_rgba(0,0,0,0.5)] backdrop-blur-xl">
      <div className="mb-3 px-3 text-xs font-bold text-white/45">
        {submenuLabels[menu]}
      </div>
      <div className="space-y-1">
        {submenuItems[menu].map((item) => (
          <button
            key={item.route}
            onClick={() => handleRouteClick(item.route)}
            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-white/75 transition-colors hover:bg-[#00F5D4]/10 hover:text-[#00F5D4]"
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  const isLegalPage = currentRoute === 'privacy' || currentRoute === 'terms' || currentRoute === 'commercial';
  const isServicesActive = currentRoute === 'services' || currentRoute === 'song-submission';
  const isDomilyActive = currentRoute === 'domily' || currentRoute === 'domi';

  if (isLegalPage) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 h-[72px] bg-[#080A0A]/92 backdrop-blur-xl border-b border-white/[0.07]">
        <div className="h-full max-w-5xl mx-auto px-5 md:px-8 flex items-center gap-5">
          <button
            onClick={onBackFromLegal}
            className="group flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white transition-colors cursor-pointer shrink-0"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            戻る
          </button>
          <span className="w-px h-5 bg-white/10" />
          <div className="flex items-center gap-3 text-sm md:text-base font-bold truncate">
            <span className="text-white/45">Koemoriについて</span>
            <span className="text-white/25">/</span>
            <span className="text-white truncate">{legalTitles[currentRoute]}</span>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      id="navbar"
      className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center border-b transition-all duration-300 ${
        (isScrolled || currentRoute !== 'home')
          ? 'bg-[#080A0A] backdrop-blur-xl border-white/[0.07] shadow-[0_10px_40px_rgba(0,0,0,0.16)]'
          : 'bg-transparent border-transparent'
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <button onClick={handleHome} className="flex items-center gap-3 cursor-pointer group" aria-label="Koemori トップへ戻る">
          <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/10 shadow-[0_0_22px_rgba(0,245,212,0.15)] group-hover:scale-105 transition-transform">
            <img src="/logo.webp" alt="Koemori Logo" className="w-full h-full object-cover" />
          </div>
          <span className="text-xl font-black tracking-tight text-white group-hover:text-[#00F5D4] transition-colors">Koemori</span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          <ul className="flex items-center gap-8 text-sm font-semibold text-white/60">
            <li>
              <button onClick={() => handleNavClick('hero')} className={`relative py-2 cursor-pointer hover:text-white transition-colors ${currentRoute === 'home' && activeSection === 'hero' ? 'text-[#00F5D4]' : ''}`}>
                ホーム
                <span className={`absolute left-1/2 -translate-x-1/2 -bottom-1 w-1 h-1 rounded-full bg-[#00F5D4] transition-opacity ${currentRoute === 'home' && activeSection === 'hero' ? 'opacity-100' : 'opacity-0'}`} />
              </button>
            </li>
            <li className="relative" onMouseEnter={() => setSubmenuOpen('services')} onMouseLeave={() => setSubmenuOpen(null)}>
              <button
                onClick={() => handleTopPageClick('services')}
                className={`relative flex items-center gap-1 py-2 cursor-pointer hover:text-white transition-colors ${isServicesActive ? 'text-[#00F5D4]' : ''}`}
              >
                ユーザーサービス <ChevronDown className={`h-3.5 w-3.5 transition-transform ${submenuOpen === 'services' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'services' && dropdownPanel('services')}
            </li>
            <li className="relative" onMouseEnter={() => setSubmenuOpen('domily')} onMouseLeave={() => setSubmenuOpen(null)}>
              <button
                onClick={() => handleTopPageClick('domily')}
                className={`relative flex items-center gap-1 py-2 cursor-pointer hover:text-white transition-colors ${isDomilyActive ? 'text-[#00F5D4]' : ''}`}
              >
                Domilyについて <ChevronDown className={`h-3.5 w-3.5 transition-transform ${submenuOpen === 'domily' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'domily' && dropdownPanel('domily')}
            </li>
            <li className="relative" onMouseEnter={() => setSubmenuOpen('about')} onMouseLeave={() => setSubmenuOpen(null)}>
              <button onClick={() => handleTopPageClick('about')} className={`relative flex items-center gap-1 py-2 cursor-pointer hover:text-white transition-colors ${activeSection === 'about' || currentRoute === 'about' ? 'text-[#00F5D4]' : ''}`}>
                Koemoriについて <ChevronDown className={`h-3.5 w-3.5 transition-transform ${submenuOpen === 'about' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'about' && dropdownPanel('about')}
            </li>
          </ul>

          <AuraButton onClick={() => handleNavClick('download')} roundedClass="rounded-full" paddingClass="px-5 py-2.5" className="text-xs" variant="primary">
            今すぐ体験
          </AuraButton>
        </div>

        <button onClick={() => setMobileMenuOpen((previous) => !previous)} className="md:hidden p-2 text-white/80 hover:text-white transition-colors cursor-pointer" aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed top-[72px] left-0 right-0 min-h-[calc(100vh-72px)] bg-[#090B0B]/98 backdrop-blur-xl p-6 z-40 border-t border-white/[0.06]">
          <div className="flex flex-col gap-1">
            <button onClick={() => handleNavClick('hero')} className="text-lg font-bold text-left py-4 border-b border-white/[0.06] text-white/75">ホーム</button>
            <div className="border-b border-white/[0.06] py-3">
              <button onClick={() => setSubmenuOpen((previous) => previous === 'services' ? null : 'services')} className="flex w-full items-center justify-between py-2 text-lg font-bold text-left text-white/75">
                ユーザーサービス <ChevronDown className={`h-4 w-4 transition-transform ${submenuOpen === 'services' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'services' && (
                <div className="mt-2 space-y-1 pl-3">
                  <button onClick={() => handleRouteClick('services')} className="block w-full py-2 text-left text-sm text-white/60">サービス一覧</button>
                  {serviceItems.map((item) => (
                    <button key={item.route} onClick={() => handleRouteClick(item.route)} className="block w-full py-2 text-left text-sm text-white/60">
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="border-b border-white/[0.06] py-3">
              <button onClick={() => setSubmenuOpen((previous) => previous === 'domily' ? null : 'domily')} className="flex w-full items-center justify-between py-2 text-lg font-bold text-left text-white/75">
              Domilyについて <ChevronDown className={`h-4 w-4 transition-transform ${submenuOpen === 'domily' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'domily' && (
                <div className="mt-2 space-y-1 pl-3">
                  <button onClick={() => handleRouteClick('domily')} className="block w-full py-2 text-left text-sm text-white/60">メンバー一覧</button>
                  {domilyItems.map((item) => (
                    <button key={item.route} onClick={() => handleRouteClick(item.route)} className="block w-full py-2 text-left text-sm text-white/60">
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="border-b border-white/[0.06] py-3">
              <button onClick={() => setSubmenuOpen((previous) => previous === 'about' ? null : 'about')} className="flex w-full items-center justify-between py-2 text-lg font-bold text-left text-white/75">
                Koemoriについて <ChevronDown className={`h-4 w-4 transition-transform ${submenuOpen === 'about' ? 'rotate-180' : ''}`} />
              </button>
              {submenuOpen === 'about' && <div className="mt-2 space-y-1 pl-3">{legalItems.map((item) => <button key={item.route} onClick={() => handleRouteClick(item.route)} className="block w-full py-2 text-left text-sm text-white/60">{item.label}</button>)}</div>}
            </div>
            <button onClick={() => handleNavClick('download')} className="w-full mt-6 text-center font-black py-4 rounded-full bg-gradient-to-r from-[#00F5D4] to-[#00BFA0] text-[#03110F] shadow-[0_0_30px_rgba(0,245,212,0.2)]">今すぐ体験</button>
          </div>
        </div>
      )}
    </nav>
  );
}
